import {
  Card,
  CardBody,
  CardHeader,
  Divider,
  HStack,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  BankIdentityRole,
  useGetBankIdentitiesByCompanyQuery,
  useGetSpvDraftQuery,
} from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency } from '@blockpulse3/helpers';
import {
  BankDetailsCard,
  ErrorQueryModal,
  ResponsiveModal,
  ResponsiveModalProps,
  SkeletonModal,
} from '@blockpulse3/ui/commons';

type Props = Omit<ResponsiveModalProps, 'children'>;

export function SPVMatriculationBankDetailsModal({ ...props }: Props): JSX.Element {
  const i18nSPVMatriculationBankDetailsModal = useTranslations('SPVMatriculationBankDetailsModal');

  const { id = '' } = useParams();

  const spvDraftReq = useGetSpvDraftQuery({ variables: { id } });
  const bankIdentitiesReq = useGetBankIdentitiesByCompanyQuery({
    variables: {
      companyId: spvDraftReq.data?.spvDraft.company.id || '',
    },
    skip: !spvDraftReq.data?.spvDraft.company.id,
  });

  if (spvDraftReq.loading || bankIdentitiesReq.loading) {
    return (
      <ResponsiveModal {...props}>
        <ModalOverlay />
        <SkeletonModal height="400px" />
      </ResponsiveModal>
    );
  }

  if (spvDraftReq.error || bankIdentitiesReq.error) {
    return (
      <ResponsiveModal {...props}>
        <ModalOverlay />
        <ErrorQueryModal height="400px" />
      </ResponsiveModal>
    );
  }

  const bankIdentities = bankIdentitiesReq?.data?.getBankIdentitiesByCompany || [];
  const bankCapitalAccount = bankIdentities.find(
    (bank) => bank.role === BankIdentityRole.SHAREHOLDER,
  )?.bankCapitalAccount;
  const capitalDepositTotal =
    spvDraftReq?.data?.spvDraft.content?.initialShareholder?.capitalDeposit;

  return (
    <ResponsiveModal {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18nSPVMatriculationBankDetailsModal('ModalTitle')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody as={Stack} spacing="4">
          <Text fontSize="sm">
            {i18nSPVMatriculationBankDetailsModal.rich('ModalSubtitle', {
              important: (chunk) => <b>{chunk}</b>,
            })}
          </Text>
          <BankDetailsCard
            bankName={bankCapitalAccount?.bank}
            iban={bankCapitalAccount?.iban}
            recipientAddress={bankCapitalAccount?.address}
            recipientName={bankCapitalAccount?.holderName}
            swift={bankCapitalAccount?.swift}
            title={`${i18nSPVMatriculationBankDetailsModal('ModalCardTitle')} ${spvDraftReq.data
              ?.spvDraft.content.name}`}
          />
          <Card bg="gray.50" boxShadow="none">
            <CardHeader as={Stack}>
              <Text fontSize="md" fontWeight="600">
                {i18nSPVMatriculationBankDetailsModal('CapitalDepositTitle')}
              </Text>
              <HStack justify="space-between">
                <Text color="gray.500" fontSize="sm" fontWeight="600">
                  {i18nSPVMatriculationBankDetailsModal('CapitalDepositShares')}
                </Text>
                <Text fontSize="sm" fontWeight="600">
                  {formatNumberCurrency(capitalDepositTotal)}
                </Text>
              </HStack>
            </CardHeader>
            <CardBody as={Stack} spacing="2">
              <Divider />
              <HStack justifyContent="space-between" pt="1">
                <Text fontSize="md" fontWeight="600">
                  {i18nSPVMatriculationBankDetailsModal('CapitalDepositTotal')}
                </Text>
                <Text fontSize="md" fontWeight="600">
                  {formatNumberCurrency(capitalDepositTotal)}
                </Text>
              </HStack>
            </CardBody>
          </Card>
        </ModalBody>
      </ModalContent>
    </ResponsiveModal>
  );
}

export type SPVMatriculationBankDetailsModalProps = Props;
